import React from "react";

/**
 * Centralized color styles utility for board components
 * Provides consistent color mapping across the application
 */

export type ColorName =
  | "default"
  | "gray"
  | "brown"
  | "orange"
  | "yellow"
  | "green"
  | "blue"
  | "purple"
  | "pink"
  | "red";

export interface BaseColorStyles {
  bg: string;
  text: string;
  dot: string;
}

export interface ColorStylesWithBadge extends BaseColorStyles {
  badge: string;
  border: string;
  hover: string;
}

export interface ColorStylesWithBorder {
  bg: string;
  text: string;
  border: string;
}

export interface ColorStylesMinimal {
  bg: string;
  text: string;
}

export interface ColorStylesBgDot {
  bg: string;
  dot: string;
}

/**
 * Base color map with RGB/RGBA values
 * This is the most commonly used format across board components
 */
// Color roles per palette entry:
//   bg   = board column background → --ca-{c}BacPriTra (faintest)
//   text = label text             → --c-{c}TexPri
//   dot  = status dot             → --c-{c}IcoAccPri (solid accent)
// The badge/pill background (--ca-{c}BacTerTra) is added by getColorStylesWithBadge.
const BASE_COLOR_MAP: Record<ColorName, BaseColorStyles> = {
  default: {
    bg: "var(--ca-bacSecTra)",
    text: "var(--c-graTexPri)",
    dot: "var(--c-graIcoAccPri)"
  },
  gray: {
    bg: "var(--ca-bacSecTra)",
    text: "var(--c-graTexPri)",
    dot: "var(--c-graIcoAccPri)"
  },
  brown: {
    bg: "var(--ca-broBacPriTra)",
    text: "var(--c-broTexPri)",
    dot: "var(--c-broIcoAccPri)"
  },
  orange: {
    bg: "var(--ca-oraBacPriTra)",
    text: "var(--c-oraTexPri)",
    dot: "var(--c-oraIcoAccPri)"
  },
  yellow: {
    bg: "var(--ca-yelBacPriTra)",
    text: "var(--c-yelTexPri)",
    dot: "var(--c-yelIcoAccPri)"
  },
  green: {
    bg: "var(--ca-greBacPriTra)",
    text: "var(--c-greTexPri)",
    dot: "var(--c-greIcoAccPri)"
  },
  blue: {
    bg: "var(--ca-bluBacPriTra)",
    text: "var(--c-bluTexPri)",
    dot: "var(--c-bluIcoAccPri)"
  },
  purple: {
    bg: "var(--ca-purBacPriTra)",
    text: "var(--c-purTexPri)",
    dot: "var(--c-purIcoAccPri)"
  },
  pink: {
    bg: "var(--ca-pinBacPriTra)",
    text: "var(--c-pinTexPri)",
    dot: "var(--c-pinIcoAccPri)"
  },
  red: {
    bg: "var(--ca-redBacPriTra)",
    text: "var(--c-redTexPri)",
    dot: "var(--c-redIcoAccPri)"
  },
} as const;

/** Badge/pill background per color → --ca-{c}BacTerTra. */
const BADGE_BG_MAP: Record<ColorName, string> = {
  default: "var(--ca-graBacTerTra)",
  gray: "var(--ca-graBacTerTra)",
  brown: "var(--ca-broBacTerTra)",
  orange: "var(--ca-oraBacTerTra)",
  yellow: "var(--ca-yelBacTerTra)",
  green: "var(--ca-greBacTerTra)",
  blue: "var(--ca-bluBacTerTra)",
  purple: "var(--ca-purBacTerTra)",
  pink: "var(--ca-pinBacTerTra)",
  red: "var(--ca-redBacTerTra)",
} as const;

/** Color border per color → --ca-{c}BorSecTra. */
const BORDER_MAP: Record<ColorName, string> = {
  default: "var(--ca-graBorSecTra)",
  gray: "var(--ca-graBorSecTra)",
  brown: "var(--ca-broBorSecTra)",
  orange: "var(--ca-oraBorSecTra)",
  yellow: "var(--ca-yelBorSecTra)",
  green: "var(--ca-greBorSecTra)",
  blue: "var(--ca-bluBorSecTra)",
  purple: "var(--ca-purBorSecTra)",
  pink: "var(--ca-pinBorSecTra)",
  red: "var(--ca-redBorSecTra)",
} as const;

/** Subtle hover background → --ca-{c}BacIntTra (a touch darker than the column bg). */
const HOVER_MAP: Record<ColorName, string> = {
  default: "var(--ca-graBacIntTra)",
  gray: "var(--ca-graBacIntTra)",
  brown: "var(--ca-broBacIntTra)",
  orange: "var(--ca-oraBacIntTra)",
  yellow: "var(--ca-yelBacIntTra)",
  green: "var(--ca-greBacIntTra)",
  blue: "var(--ca-bluBacIntTra)",
  purple: "var(--ca-purBacIntTra)",
  pink: "var(--ca-pinBacIntTra)",
  red: "var(--ca-redBacIntTra)",
} as const;

/** Tinted panel background per color → --ca-{c}BacSecTra. */
const SURFACE_MAP: Record<ColorName, string> = {
  default: "var(--ca-graBacSecTra)",
  gray: "var(--ca-graBacSecTra)",
  brown: "var(--ca-broBacSecTra)",
  orange: "var(--ca-oraBacSecTra)",
  yellow: "var(--ca-yelBacSecTra)",
  green: "var(--ca-greBacSecTra)",
  blue: "var(--ca-bluBacSecTra)",
  purple: "var(--ca-purBacSecTra)",
  pink: "var(--ca-pinBacSecTra)",
  red: "var(--ca-redBacSecTra)",
} as const;

/**
 * A tinted panel plus a matching accent. The middle step between `bg` (3–6%,
 * too faint to read as a fill at card size) and `badge` (~20%, a wash over
 * anything larger than a pill).
 */
export function getColorSurface(
  colorName: ColorName | string = "default",
): { surface: string; accent: string } {
  const color = (colorName in SURFACE_MAP ? colorName : "default") as ColorName;
  return { surface: SURFACE_MAP[color], accent: BASE_COLOR_MAP[color].dot };
}

/**
 * Get color styles with bg, text, and dot
 * This is the most common format used across board components
 */
export function getColorStyles(colorName: ColorName | string = "default"): BaseColorStyles {
  const color = (colorName in BASE_COLOR_MAP ? colorName : "default") as ColorName;
  return BASE_COLOR_MAP[color] || BASE_COLOR_MAP.default;
}

/**
 * Get color styles with badge (same as bg)
 * Used in boardView.tsx
 */
export function getColorStylesWithBadge(colorName: ColorName | string = "default"): ColorStylesWithBadge {
  const color = (colorName in BADGE_BG_MAP ? colorName : "default") as ColorName;
  const base = getColorStyles(color);
  return {
    ...base,
    badge: BADGE_BG_MAP[color],
    border: BORDER_MAP[color],
    hover: HOVER_MAP[color],
  };
}

/**
 * Pill/tag color styles: same shape as getColorStyles, but bg is the badge
 * background (--ca-{c}BacTerTra) instead of the faint board bg. Drop-in for
 * option/tag chips across the views.
 */
export function getBadgeColorStyles(colorName: ColorName | string = "default"): BaseColorStyles {
  const c = getColorStylesWithBadge(colorName);
  return { bg: c.badge, text: c.text, dot: c.dot };
}

/** Id-keyed property schemas or row values; shapes vary per property type. */
type PropertyBag = Record<string, any>;

function resolveStatusColorName(
  properties: PropertyBag | undefined,
  rowProperties: PropertyBag | undefined,
): string | null {
  if (!properties || !rowProperties) return null;

  const entries = Object.entries(properties);
  const statusEntry =
    entries.find(([, p]) => p?.type === "status" && p?.default === true) ??
    entries.find(([, p]) => p?.type === "status");
  if (!statusEntry) return null;

  const [propId, schema] = statusEntry;
  const raw = rowProperties[propId];
  // A status value is an option id, or an object carrying one.
  const value =
    raw && typeof raw === "object" ? (raw.id ?? raw.name) : raw;
  if (value === undefined || value === null || value === "") return null;

  const option = schema?.options?.find(
    (o: any) => o && String(o.id) === String(value),
  );
  if (!option) return null;

  return option.color || "default";
}

/**
 * The tint a row takes from its status option — the SAME fill/text pair the
 * status pill uses, so a tinted card and its own pill can never disagree.
 *
 * Prefers the datasource's default status property (the one the board groups
 * by), falling back to the first status property so a datasource that never
 * marked a default still colors. Returns null when there is no status
 * property, no value, or the value points at a deleted option — callers keep
 * the neutral card surface in that case rather than inventing a color.
 */
export function getStatusTint(
  properties: PropertyBag | undefined,
  rowProperties: PropertyBag | undefined,
): BaseColorStyles | null {
  const color = resolveStatusColorName(properties, rowProperties);
  return color === null ? null : getBadgeColorStyles(color);
}

/**
 * `getStatusTint`'s color at surface weight instead of chip weight, for panels.
 * Null on the same conditions, so callers fall back to the neutral surface.
 */
export function getStatusSurface(
  properties: PropertyBag | undefined,
  rowProperties: PropertyBag | undefined,
): { surface: string; accent: string } | null {
  const color = resolveStatusColorName(properties, rowProperties);
  return color === null ? null : getColorSurface(color);
}

/**
 * Get color styles with only bg and text (no dot)
 * Used in calendarCard.tsx and boardViewCard.tsx
 */
export function getColorStylesMinimal(colorName: ColorName | string = "default"): ColorStylesMinimal {
  const base = getColorStyles(colorName);
  return {
    bg: base.bg,
    text: base.text,
  };
}

/**
 * Get color styles with only bg and dot (no text)
 * Used in editSinglePropertyModal.tsx (but with Tailwind classes)
 */
export function getColorStylesBgDot(colorName: ColorName | string = "default"): ColorStylesBgDot {
  const base = getColorStyles(colorName);
  return {
    bg: base.bg,
    dot: base.dot,
  };
}

/**
 * Get color styles formatted for React style prop
 */
export function getColorStylesAsCss(colorName: ColorName | string = "default"): React.CSSProperties {
  const styles = getColorStyles(colorName);
  return {
    backgroundColor: styles.bg,
    color: styles.text,
  };
}

/**
 * Export the base color map for direct access if needed
 */
export { BASE_COLOR_MAP };

